import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { useLegacyEffect } from '@/molecules/utils'
import { getContentfulClient } from '@/services/ApolloClient'
import type { BlogPost } from '@/services/BlogService'
import { getMorePostsQuery } from '@/services/BlogService'
import { GetMorePostsQuery } from '@/types/codegen-contentful'
import { useSafeTrack } from '@/utils/analytics'
import BlogGridSection from './BlogGridSection'
import BlogHeroSection from './BlogHeroSection'
import BlogHighlightSection from './BlogHighlightSection'

interface BlogViewProps {
  posts: BlogPost[]
  project?: string
}

const BlogView: React.FC<BlogViewProps> = ({ posts, project }) => {
  const client = getContentfulClient()
  const track = useSafeTrack()
  const [skip, setSkip] = useState(6)
  const [loading, setLoading] = useState(false)
  const [morePosts, setMorePosts] = useState<BlogPost[]>(posts.slice(2))
  const observerRef = useRef(null)

  const loadMorePosts = useCallback(async () => {
    try {
      setLoading(true)
      const { data } = await client.query<GetMorePostsQuery>({
        query: getMorePostsQuery,
        variables: { preview: false, project, skip },
        errorPolicy: 'all',
      })

      if ((data?.postCollection?.items.length ?? 0) > 0) {
        setSkip((previous) => previous + 6)
        setMorePosts((previous) => [...previous, ...(data?.postCollection?.items?.filter(Boolean) ?? [])] as BlogPost[])
      }
    } finally {
      setLoading(false)
    }
  }, [client, skip, project])

  useEffect(() => {
    const observerRefValue = observerRef.current

    const observer = new IntersectionObserver(
      (entries) => {
        if (!loading) {
          const entry = entries[0]
          if (entry?.isIntersecting) {
            loadMorePosts()
          }
        }
      },
      { rootMargin: '0px 0px 352px 0px' },
    )

    if (observerRef.current) {
      observer.observe(observerRef.current)
    }

    return () => {
      if (observerRefValue) {
        observer.unobserve(observerRefValue)
      }
    }
  }, [loading, loadMorePosts, observerRef])

  const firstPost = useMemo(() => {
    return posts?.[0]
  }, [posts])

  useLegacyEffect(() => {
    track('Blog Page Viewed')
  }, [track])

  return (
    <div className="min-h-screen bg-white">
      {firstPost && (
        <>
          <BlogHeroSection post={firstPost} project={project} />
          <PaddedContainer>
            {posts?.length > 1 && <BlogHighlightSection posts={posts.slice(0, 2)} project={project} />}
            {morePosts?.length > 0 && <BlogGridSection loading={loading} posts={morePosts} project={project} />}
          </PaddedContainer>
          <div ref={observerRef} />
        </>
      )}
    </div>
  )
}

export default BlogView
